import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authApi, saveAuthSession } from '../services/api';
import '../styles/AuthPages.css';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = await authApi.login({
        email: email.trim(),
        password,
      });

      saveAuthSession({ token: payload.token, user: payload.user });
      setStatusMessage('Signed in successfully! Redirecting you to your dashboard...');

      const redirectPath = location.state?.from?.pathname;

      window.setTimeout(() => {
        if (redirectPath) {
          navigate(redirectPath);
        } else if (payload.user?.role === 'admin' || payload.user?.role === 'superadmin') {
          navigate('/admin-dashboard');
        } else if (payload.user?.role === 'physiotherapist' || payload.user?.role === 'therapist') {
          navigate('/physio-dashboard');
        } else {
          navigate('/client-dashboard');
        }
      }, 900);
    } catch (error) {
      setErrorMessage(error.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="zen-login-page">
      {/* Soft Ambient Floating Glows */}
      <div className="zen-ambient-glow zen-glow-1"></div>
      <div className="zen-ambient-glow zen-glow-2"></div>

      <div className="zen-login-container">
        {/* Brand Pill */}
        <div className="zen-brand-capsule">
          <span className="zen-pulse-indicator"></span>
          PSYCHMITRA SECURE PORTAL
        </div>

        {/* Elevated Wellness Card */}
        <div className="zen-login-card">
          {/* Card Header */}
          <div className="zen-card-header">
            <h1 className="zen-card-title">Welcome Back</h1>
            <p className="zen-card-subtitle">
              Sign in to access your appointments, therapy sessions, and wellness resources.
            </p>
          </div>

          {/* Feedback Alerts */}
          {errorMessage && (
            <div className="hb-alert hb-alert-error">
              <i className="fas fa-circle-exclamation"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          {statusMessage && (
            <div className="hb-alert hb-alert-success">
              <i className="fas fa-circle-check"></i>
              <span>{statusMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email Field */}
            <div className="zen-form-group">
              <label className="zen-label" htmlFor="loginEmail">
                Email Address
              </label>
              <div className="zen-input-wrap">
                <i className="fas fa-envelope zen-input-icon"></i>
                <input
                  id="loginEmail"
                  type="email"
                  className={`zen-input ${validationErrors.email ? 'has-error' : ''}`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (validationErrors.email) {
                      setValidationErrors((prev) => ({ ...prev, email: '' }));
                    }
                  }}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {validationErrors.email && (
                <span className="zen-error-msg">{validationErrors.email}</span>
              )}
            </div>

            {/* Password Field */}
            <div className="zen-form-group">
              <label className="zen-label" htmlFor="loginPassword">
                Password
              </label>
              <div className="zen-input-wrap">
                <i className="fas fa-lock zen-input-icon"></i>
                <input
                  id="loginPassword"
                  type={showPassword ? 'text' : 'password'}
                  className={`zen-input ${validationErrors.password ? 'has-error' : ''}`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (validationErrors.password) {
                      setValidationErrors((prev) => ({ ...prev, password: '' }));
                    }
                  }}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="zen-pw-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {validationErrors.password && (
                <span className="zen-error-msg">{validationErrors.password}</span>
              )}
            </div>

            {/* Remember Me & Forgot Password Row */}
            <div className="zen-options-row">
              <label className="zen-remember-wrap">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember this device</span>
              </label>

              <Link to="/forgot-password" className="zen-forgot-link">
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="zen-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
          </form>

          {/* Footer Options */}
          <div className="zen-divider">
            <span>or</span>
          </div>

          <div className="zen-card-footer">
            <p style={{ margin: 0 }}>
              Don't have an account yet?{' '}
              <Link to="/register">Create an account</Link>
            </p>
          </div>
        </div>

        {/* Reassurance Badges */}
        <div className="zen-trust-row">
          <div className="zen-trust-item">
            <i className="fas fa-shield-halved" style={{ color: '#059669' }}></i>
            <span>256-Bit SSL Encrypted</span>
          </div>
          <div className="zen-trust-item">
            <i className="fas fa-lock" style={{ color: '#7c4bb9' }}></i>
            <span>100% Confidential</span>
          </div>
          <div className="zen-trust-item">
            <i className="fas fa-certificate" style={{ color: '#2563eb' }}></i>
            <span>HIPAA Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
